// Function index:
//   installWorldUi(deps) — wire interior indicator + doorway enter prompt UI.
//   WorldUiHandle           — teardown helper + position updater.

import type { BootstrapResponse } from '../api.js';
import type { WS } from '../ws.js';

export interface WorldUiHandle {
  destroy(): void;
  updateSelfPosition(q: number, r: number): void;
}

export function installWorldUi(deps: {
  boot: BootstrapResponse;
  ws: WS;
  isPaused: () => boolean;
  onEnter: (locationId: number | null) => void;
}): WorldUiHandle {
  const { boot, ws, isPaused, onEnter } = deps;
  const interiorIndicator = document.getElementById('interior-indicator')!;
  const interiorName = document.getElementById('interior-name')!;
  const exitInteriorBtn = document.getElementById('exit-interior-btn') as HTMLButtonElement;
  const enterPrompt = document.getElementById('enter-prompt')!;
  const enterName = document.getElementById('enter-prompt-name')!;
  const enterBtn = document.getElementById('enter-prompt-btn') as HTMLButtonElement;

  const isInterior = boot.world.parent_world_id != null;
  interiorIndicator.classList.toggle('visible', isInterior);
  interiorName.textContent = boot.world.name;

  let currentEnterLocationId: number | null = null;

  const updateEnterPrompt = (q: number, r: number): void => {
    // Unmapped interiors are enterable too: the server generates them JIT on
    // first entry (world/transition/enter.ts — race-safe, with a flavour line
    // while it builds, and a graceful "stays shut" on failure). Filtering on
    // interior_world_id here sealed every structural-pass settlement forever.
    const here = boot.locations.find((l) => isOnOrAdjacent(l.q, l.r, q, r));
    if (here) {
      currentEnterLocationId = here.id;
      enterName.textContent = here.name;
      enterPrompt.classList.add('visible');
    } else {
      currentEnterLocationId = null;
      enterPrompt.classList.remove('visible');
    }
  };

  const onEnterClick = (): void => { onEnter(currentEnterLocationId); };
  enterBtn.addEventListener('click', onEnterClick);

  const onExitClick = (): void => { if (!isPaused()) ws.send('player.exit'); };
  exitInteriorBtn.addEventListener('click', onExitClick);

  return {
    destroy() {
      interiorIndicator.classList.remove('visible');
      enterPrompt.classList.remove('visible');
      exitInteriorBtn.removeEventListener('click', onExitClick);
      enterBtn.removeEventListener('click', onEnterClick);
    },
    updateSelfPosition(q: number, r: number): void {
      updateEnterPrompt(q, r);
    },
  };
}

function isOnOrAdjacent(lq: number, lr: number, q: number, r: number): boolean {
  const dq = lq - q;
  const dr = lr - r;
  return Math.max(Math.abs(dq), Math.abs(dr), Math.abs(dq + dr)) <= 1;
}
